import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPanel from './FilterPanel';
import { getPrimaryProductImage } from '../utils/productImages';

// Search icon
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

// Descending filter lines — classic funnel icon
const FilterIcon = () => (
  <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="0.5" y1="1" x2="15.5" y2="1" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
    <line x1="2.5" y1="6.5" x2="13.5" y2="6.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
    <line x1="5" y1="12" x2="11" y2="12" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
  </svg>
);

function SearchBar({
  value, onChange, liveResults,
  onFilterToggle, filterActive, filterOpen,
  filterMode, filterProducts, filterCategories,
  filters, onFiltersChange,
}) {
  const [focused, setFocused] = useState(false);
  const containerRef = useRef(null);
  const filterTimerRef = useRef(null);
  const filterOpenRef = useRef(filterOpen);
  useEffect(() => { filterOpenRef.current = filterOpen; }, [filterOpen]);

  const showDropdown = focused && value.trim() && liveResults && liveResults.length > 0;

  // Close when clicking outside the entire search-bar container
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onFilterToggle();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterEnter = () => {
    clearTimeout(filterTimerRef.current);
    if (!filterOpenRef.current) onFilterToggle();
  };

  const handleFilterLeave = () => {
    filterTimerRef.current = setTimeout(() => {
      if (filterOpenRef.current) onFilterToggle();
    }, 500);
  };

  return (
    <div className={`search-bar${onFilterToggle ? ' has-filter' : ''}`} ref={containerRef}>
      <span className="search-icon">
        <SearchIcon />
      </span>
      <input
        type="text"
        placeholder="Search products..."
        value={value}
        onChange={e => onChange(e.target.value)}
        className="search-input"
        aria-label="Search products"
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
      />
      {value && (
        <button
          className="search-clear"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          ✕
        </button>
      )}
      {onFilterToggle && (
        <div
          className="filter-hover-zone"
          onMouseEnter={handleFilterEnter}
          onMouseLeave={handleFilterLeave}
        >
          <button
            className={`search-filter-btn${filterActive ? ' active' : ''}`}
            onClick={onFilterToggle}
            aria-label="Toggle filters"
            title="Filter products"
          >
            <FilterIcon />
            {filterActive && <span className="sfb-dot" />}
          </button>

          {/* Floating filter dropdown — overlays content, does NOT push page down */}
          {filterOpen && filterMode && (
            <FilterPanel
              mode={filterMode}
              products={filterProducts}
              categories={filterCategories}
              filters={filters}
              onFiltersChange={onFiltersChange}
            />
          )}
        </div>
      )}

      {/* Live search dropdown */}
      {showDropdown && (
        <div className="search-live-dropdown">
          {liveResults.map(p => {
            const img = getPrimaryProductImage(p);
            return (
              <Link
                key={p.id}
                to={`/product/${p.slug || p.id}`}
                className="search-live-item"
                onClick={() => { onChange(''); setFocused(false); }}
              >
                {img && <img src={img} alt={p.name} className="search-live-img" loading="lazy" />}
                <div className="search-live-info">
                  <span className="search-live-name">{p.name}</span>
                  <span className="search-live-price">KES {parseFloat(p.price).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
